/**
 * Tiny global toast queue.
 *
 * Imported anywhere via `useToast()`; the actual rendering lives in
 * `AppToast.vue`, which must be mounted once in the app shell. Keeping
 * state in a module-scope reactive array (not a Pinia store) avoids a
 * cross-feature dependency for what is purely UI plumbing.
 */
import { reactive, readonly } from 'vue'

export type ToastVariant = 'default' | 'success' | 'error' | 'warning'

export interface ToastEntry {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  /** Auto-dismiss after this many ms. 0 = sticky. */
  duration: number
}

interface ToastOptions {
  title: string
  description?: string
  variant?: ToastVariant
  duration?: number
}

const toasts = reactive<ToastEntry[]>([])

/**
 * Cap on simultaneous toasts. Bulk operations that fail per-row
 * (media upload of 50 files with 30 errors) used to spawn 30 stacked
 * toasts, burying the page. 5 is the practical max a user can take
 * in at a glance — older toasts are evicted on overflow.
 */
const MAX_TOASTS = 5

/**
 * Suppress identical toasts arriving in quick succession. If the
 * same (title, description, variant) tuple is already at the bottom
 * of the queue, drop the duplicate instead of stacking it — a
 * mutation chain that fails the same way for every row otherwise
 * fills the screen with carbon copies.
 */
const DEDUP_WINDOW_MS = 1500
function isDuplicate(opts: ToastOptions, now: number): boolean {
  const last = toasts[toasts.length - 1]
  if (!last) return false
  const lastTs = lastSeenAt.get(last.id) ?? 0
  if (now - lastTs > DEDUP_WINDOW_MS) return false
  return (
    last.title === opts.title &&
    last.description === opts.description &&
    last.variant === (opts.variant ?? 'default')
  )
}
const lastSeenAt = new Map<string, number>()

function show(opts: ToastOptions): string {
  const now = Date.now()
  if (isDuplicate(opts, now)) {
    // Bump the existing one's timestamp so the dedup window slides.
    const last = toasts[toasts.length - 1]
    if (last) {
      lastSeenAt.set(last.id, now)
      return last.id
    }
  }
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  toasts.push({
    id,
    title: opts.title,
    ...(opts.description !== undefined ? { description: opts.description } : {}),
    variant: opts.variant ?? 'default',
    duration: opts.duration ?? 4000,
  })
  lastSeenAt.set(id, now)
  // Cap — evict the oldest. Capacity check fires after the push so a
  // single overflow only ever removes the head, not the just-shown
  // toast the user is about to read.
  while (toasts.length > MAX_TOASTS) {
    const evicted = toasts.shift()
    if (evicted) lastSeenAt.delete(evicted.id)
  }
  return id
}

function dismiss(id: string): void {
  const i = toasts.findIndex((t) => t.id === id)
  if (i !== -1) {
    toasts.splice(i, 1)
    lastSeenAt.delete(id)
  }
}

function clear(): void {
  toasts.splice(0, toasts.length)
  lastSeenAt.clear()
}

export function useToast() {
  return {
    toasts: readonly(toasts) as readonly ToastEntry[],
    show,
    dismiss,
    clear,
    success: (title: string, description?: string) =>
      show({ title, ...(description !== undefined ? { description } : {}), variant: 'success' }),
    error: (title: string, description?: string) =>
      show({ title, ...(description !== undefined ? { description } : {}), variant: 'error' }),
    warning: (title: string, description?: string) =>
      show({ title, ...(description !== undefined ? { description } : {}), variant: 'warning' }),
  }
}
